import { AdminRatesConfig, DEFAULT_ADMIN_RATES } from "./adminRates";

const BACKEND_URL =
  (process.env.REACT_APP_BACKEND_URL as string) ||
  "https://mytslmap-worker.rob3kop.workers.dev";
const SS_AUTH_TOKEN = "mytsl_authToken_v1";

const SS_ADMIN_UNLOCK = "mytsl_adminUnlock_v1";
// NOTE: Source of truth is backend users.json (per-user).
// We keep a *sessionStorage* cache only for sync access from pricing/getRate.ts
const CACHE_KEY = "adminRatesCache";

function makeCacheKey(userKey?: string) {
  const k = String(userKey || "").trim().toLowerCase();
  return k ? `${CACHE_KEY}::${k}` : CACHE_KEY;
}

function getToken(): string | null {
  try {
    return sessionStorage.getItem(SS_AUTH_TOKEN);
  } catch {
    return null;
  }
}

function getAdminUnlock(): string | null {
  try {
    return sessionStorage.getItem(SS_ADMIN_UNLOCK);
  } catch {
    return null;
  }
}

function mergeWithDefaults(parsed: Partial<AdminRatesConfig> | null | undefined): AdminRatesConfig {
  const p = parsed || ({} as any);

  // old empty data → ignore
  const allOutZero = Object.values((p as any).out || {}).every((v) => v === 0);
  const allInZero = Object.values((p as any).in || {}).every((v) => v === 0);
  const noDomestic = !(p as any).domestic || Object.keys((p as any).domestic || {}).length === 0;
  if (allOutZero && allInZero && noDomestic) return DEFAULT_ADMIN_RATES;

  return {
    out: { ...DEFAULT_ADMIN_RATES.out, ...((p as any).out || {}) } as any,
    in: { ...DEFAULT_ADMIN_RATES.in, ...((p as any).in || {}) } as any,
    domestic: { ...DEFAULT_ADMIN_RATES.domestic, ...((p as any).domestic || {}) },

    vehiclePercent: {
      ...DEFAULT_ADMIN_RATES.vehiclePercent,
      ...((p as any).vehiclePercent || {}),
    },

    shortMultiplier:
      typeof (p as any).shortMultiplier === "number" && Number.isFinite((p as any).shortMultiplier)
        ? (p as any).shortMultiplier
        : DEFAULT_ADMIN_RATES.shortMultiplier,

    shortKmThreshold:
      typeof (p as any).shortKmThreshold === "number" && Number.isFinite((p as any).shortKmThreshold)
        ? (p as any).shortKmThreshold
        : DEFAULT_ADMIN_RATES.shortKmThreshold,

    domesticShortKmThreshold: {
      ...DEFAULT_ADMIN_RATES.domesticShortKmThreshold,
      ...((p as any).domesticShortKmThreshold || {})
    }
  };
}

function loadFromSession(userKey?: string): AdminRatesConfig | null {
  try {
    const raw = sessionStorage.getItem(makeCacheKey(userKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AdminRatesConfig>;
    return mergeWithDefaults(parsed);
  } catch {
    return null;
  }
}

function saveToSession(cfg: AdminRatesConfig, userKey?: string) {
  try {
    sessionStorage.setItem(makeCacheKey(userKey), JSON.stringify(cfg));
  } catch {
    // ignore
  }
}

function clearSession(userKey?: string) {
  try {
    sessionStorage.removeItem(makeCacheKey(userKey));
  } catch {
    // ignore
  }
}

/**
 * Sync read (used by pricing/getRate.ts).
 * If cache is empty, returns defaults.
 */
export function loadAdminRates(userKey?: string): AdminRatesConfig {
  return loadFromSession(userKey) || DEFAULT_ADMIN_RATES;
}

/**
 * Fetch current rates from backend and refresh session cache.
 * Safe to call any time after login.
 */
export async function refreshAdminRates(userKey?: string): Promise<AdminRatesConfig> {
  const token = getToken();
  if (!token) return loadAdminRates(userKey);

  const res = await fetch(`${BACKEND_URL}/api/admin/rates`, {
    headers: { Authorization: `Bearer ${token}`, "x-admin-unlock": String(getAdminUnlock() || "") }
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `RATES_GET_HTTP_${res.status}`);

  const merged = mergeWithDefaults((data?.config || null) as any);
  saveToSession(merged, userKey);
  return merged;
}

/**
 * Save rates to backend (source of truth) and update session cache.
 */
export async function saveAdminRates(cfg: AdminRatesConfig, userKey?: string) {
  const token = getToken();
  if (!token) throw new Error("not_authenticated");

  const res = await fetch(`${BACKEND_URL}/api/admin/rates`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "x-admin-unlock": String(getAdminUnlock() || ""), "Content-Type": "application/json" },
    body: JSON.stringify({ config: cfg })
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `RATES_SAVE_HTTP_${res.status}`);

  saveToSession(cfg, userKey);
}

/**
 * Clear rates on backend (back to defaults) and clear session cache.
 */
export async function clearAdminRates(userKey?: string) {
  const token = getToken();
  if (!token) throw new Error("not_authenticated");

  const res = await fetch(`${BACKEND_URL}/api/admin/rates`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}`, "x-admin-unlock": String(getAdminUnlock() || "") }
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `RATES_CLEAR_HTTP_${res.status}`);

  clearSession(userKey);
}

/**
 * Optional one-time migration: if legacy localStorage exists, copy it to backend once.
 * After that, remove legacy localStorage keys.
 */
export async function migrateLegacyLocalStorageRatesToBackend(userKey?: string) {
  try {
    if (!userKey) return;

    const legacyGlobal = localStorage.getItem("adminRatesConfig");
    const legacyUser = localStorage.getItem(`adminRatesConfig::${userKey.trim().toLowerCase()}`);
    const raw = legacyUser || legacyGlobal;
    if (!raw) return;

    const parsed = JSON.parse(raw) as Partial<AdminRatesConfig>;
    const merged = mergeWithDefaults(parsed);

    await saveAdminRates(merged, userKey);

    // cleanup legacy keys (stop using localStorage)
    localStorage.removeItem("adminRatesConfig");
    localStorage.removeItem(`adminRatesConfig::${userKey.trim().toLowerCase()}`);
  } catch {
    // migration is best-effort; ignore failures
  }
}
